import { normalizeProduct } from '../../normalizers'
import type { CatalogProduct } from '../../types'
import { requestCatalogJson, toRequiredRecord } from './catalogApi.shared'

const ensurePublicProductVisible = (product: CatalogProduct, allowDraftPreview: boolean) => {
  const visibilityStatus = product.status
  if (!visibilityStatus || visibilityStatus === 'active') return
  if (allowDraftPreview && visibilityStatus === 'draft') return
  throw new Error('Prodotto non pubblicato')
}

const fetchProductRecord = async ({
  path,
  signal,
  notFoundError,
}: {
  path: string
  signal: AbortSignal
  notFoundError: string
}): Promise<Record<string, unknown>> => {
  const data = await requestCatalogJson<{ product?: unknown }>({
    path,
    signal,
    notFoundError,
    fallbackError: 'Errore caricamento prodotto',
  })
  return toRequiredRecord(data.product, notFoundError)
}

const fetchProductByPath = async ({
  path,
  signal,
  notFoundError,
  allowDraftPreview,
}: {
  path: string
  signal: AbortSignal
  notFoundError: string
  allowDraftPreview: boolean
}): Promise<CatalogProduct> => {
  const rawProduct = await fetchProductRecord({
    path,
    signal,
    notFoundError,
  })
  const normalized = normalizeProduct(rawProduct)
  if (!normalized._id) {
    throw new Error(notFoundError)
  }
  // Preview routes are the only ones allowed to read draft products from the storefront runtime.
  ensurePublicProductVisible(normalized, allowDraftPreview)
  return normalized
}

export const fetchProductBySlug = async (
  slug: string,
  signal: AbortSignal,
): Promise<CatalogProduct> =>
  fetchProductByPath({
    path: `/catalog/products/${encodeURIComponent(slug)}`,
    signal,
    notFoundError: 'Prodotto non trovato',
    allowDraftPreview: false,
  })

export const fetchProductByPublicCode = async (
  publicCode: string,
  signal: AbortSignal,
): Promise<CatalogProduct> =>
  fetchProductByPath({
    path: `/catalog/products/code/${encodeURIComponent(publicCode)}`,
    signal,
    notFoundError: 'Prodotto non trovato',
    allowDraftPreview: false,
  })

export const fetchProductById = async (
  id: string,
  signal: AbortSignal,
): Promise<CatalogProduct> =>
  fetchProductByPath({
    path: `/catalog/products/${encodeURIComponent(id)}`,
    signal,
    notFoundError: 'Anteprima prodotto non trovata',
    allowDraftPreview: true,
  })
